import React, { useContext, useState, useEffect } from "react";
import {
  Container,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Box,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useStore } from "../StoreContext/StoreContext";
import { Link, useNavigate } from "react-router-dom";
import { OrderCreateApi, OrderUpdateApi, ProductListAPI } from "../../Utility/UtilApi/UtilApi";
import UtilToastMessages from "../../Utility/UtilToastMessages/UtilToastMessages";
import { buttonColor, buttonHoverColor } from "../../UtilColorPalate/UtilColorPalate";

const CartPage = () => {
  const {
    hasExistingOrder,
    OrderRecordId,
    dealLineItems,
    fetchCurrentDeals,
    updateDealState,
    currencyCode: contextCurrencyCode,
    isLoggedIn,
    updateLocalCart,
    getAndUpdateCartCount,
  } = useStore();

  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [isSnackbarOpen, setSnackbarOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastColor, setToastColor] = useState("");
  const [loading, setLoading] = useState(false);
  const [currencyCode, setCurrencyCode] = useState(contextCurrencyCode || "$");
  const [isCartUpdated, setIsCartUpdated] = useState(false);
  const [isCheckoutEnabled, setIsCheckoutEnabled] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const data = {};
      const response = await ProductListAPI(data);
      const products = response?.data?.products || [];
      setCurrencyCode(response?.data?.currencycode || contextCurrencyCode || "$");
      return products;
    } catch (error) {
      console.error("Error fetching products:", error);
      setToastMessage("Failed to load product details.");
      setToastColor("error");
      setSnackbarOpen(true);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadCartItems = async () => {
      if (isLoggedIn && hasExistingOrder && dealLineItems.length > 0) {
        const formattedCartItems = dealLineItems.map((item) => {
          const unitPrice = item.unitpriceexclvat;
          const vatPercentage = item.vat || 0;
          const discountPercentage = item.discount || 0;
          const priceWithDiscount = unitPrice * (1 - discountPercentage / 100);
          const priceWithVat = priceWithDiscount * (1 + vatPercentage / 100);

          return {
            id: item.product.id || item.product,
            name: item.name,
            unitpriceexclvat: unitPrice,
            unitpriceincldiscount: priceWithDiscount,
            priceWithVat: priceWithVat,
            quantity: item.quantity,
            recordid: item.recordid,
            vat: vatPercentage,
            discount: discountPercentage,
            originalQuantity: item.quantity,
          };
        });
        setCartItems(formattedCartItems);
        setIsCheckoutEnabled(true);
      } else if (!isLoggedIn) {
        const storedCart = JSON.parse(localStorage.getItem("cart")) || [];
        if (storedCart.length > 0) {
          const products = await fetchProducts();
          const formattedCartItems = storedCart.map((cartItem) => {
            const product = products.find((p) => p.id === cartItem.id);
            if (!product) return null;
            const unitPrice = product.unitpriceexclvat;
            const vatPercentage = product.vat || 0;
            const discountPercentage = product.discount || 0;
            const priceWithDiscount = unitPrice * (1 - discountPercentage / 100);
            const priceWithVat = priceWithDiscount * (1 + vatPercentage / 100);

            return {
              id: product.id,
              name: product.name,
              unitpriceexclvat: unitPrice,
              unitpriceincldiscount: priceWithDiscount,
              priceWithVat: priceWithVat,
              quantity: cartItem.quantity,
              vat: vatPercentage,
              discount: discountPercentage,
              originalQuantity: cartItem.quantity,
            };
          }).filter(Boolean);
          setCartItems(formattedCartItems);
          setIsCheckoutEnabled(true);
          getAndUpdateCartCount();
        } else {
          setCartItems([]);
          setIsCheckoutEnabled(false);
          getAndUpdateCartCount();
        }
      } else {
        setCartItems([]);
        setIsCheckoutEnabled(false);
        getAndUpdateCartCount();
      }
    };

    loadCartItems();
  }, [isLoggedIn, hasExistingOrder, dealLineItems, getAndUpdateCartCount]);

  const handleQuantityChange = (id, newQuantity) => {
    const parsedQuantity = Math.max(1, parseInt(newQuantity) || 1);
    const updatedCartItems = cartItems.map((item) =>
      item.id === id ? { ...item, quantity: parsedQuantity } : item
    );
    setCartItems(updatedCartItems);

    const needsUpdate = updatedCartItems.some((item) => item.quantity !== item.originalQuantity);
    setIsCartUpdated(needsUpdate);
    setIsCheckoutEnabled(!needsUpdate && cartItems.length > 0);

    if (!isLoggedIn) {
      const updatedLocalCart = updatedCartItems.map((item) => ({
        id: item.id,
        quantity: item.quantity,
      }));
      updateLocalCart(updatedLocalCart);
    }
  };

  const formatPrice = (value) => {
    const formattedValue = value.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${currencyCode}${formattedValue}`;
  };

  const calculateSubtotal = (price, quantity) => {
    return formatPrice(price * quantity);
  };

  const calculateTotal = () => {
    const total = cartItems.reduce((total, item) => total + item.priceWithVat * item.quantity, 0);
    return formatPrice(total);
  };

  const createDeal = async (product, type) => {
    const generateName = () => `${type}_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
    const payload = {
      order: {
        name: generateName(),
        type: type,
      },
      deallineitems: [
        {
          name: product.name,
          product: product.id,
          quantity: product.quantity,
          unitpriceexclvat: product.unitpriceexclvat,
          vat: product.vat || 0,
        },
      ],
    };

    try {
      const response = await OrderCreateApi(payload);
      setToastMessage(response.message || `${type} created and product added!`);
      setToastColor(response.success ? "success" : "warning");
      setSnackbarOpen(true);
      await fetchCurrentDeals();
      getAndUpdateCartCount();
      return response;
    } catch (error) {
      console.error(`Error creating ${type}:`, error);
      setToastMessage(error?.response?.data?.message || "Failed to create order");
      setToastColor("error");
      setSnackbarOpen(true);
      throw error;
    }
  };

  const updateDeal = async (type, recordId, updatedItems) => {
    if (!recordId) {
      console.error(`${type} record ID is missing`);
      setToastMessage(`Error: No ${type} ID available.`);
      setToastColor("error");
      setSnackbarOpen(true);
      return;
    }

    const updatedLineItems = updatedItems.map((item) => ({
      recordid: item.recordid,
      name: item.name,
      product: item.id,
      quantity: item.quantity,
      unitpriceexclvat: item.unitpriceexclvat,
      vat: item.vat || 0,
    }));

    const payload = {
      order: {
        recordid: recordId,
        type: type,
        stage: "In Progress",
      },
      deallineitems: updatedLineItems,
    };

    try {
      const response = await OrderUpdateApi(payload);
      setToastMessage(response.message || `Product updated in ${type}!`);
      setToastColor(response.success ? "success" : "warning");
      setSnackbarOpen(true);
      await fetchCurrentDeals();
      getAndUpdateCartCount();
      return response;
    } catch (error) {
      console.error(`Error updating ${type}:`, error);
      setToastMessage(`Failed to update ${type}.`);
      setToastColor("error");
      setSnackbarOpen(true);
      throw error;
    }
  };

  const handleUpdateCart = async () => {
    try {
      if (isLoggedIn) {
        if (!hasExistingOrder && cartItems.length > 0) {
          await createDeal(cartItems[0], "Order");
          if (cartItems.length > 1) {
            const remainingItems = cartItems.slice(1);
            await updateDeal("Order", OrderRecordId, remainingItems);
          }
        } else if (hasExistingOrder) {
          await updateDeal("Order", OrderRecordId, cartItems);
        }
      } else {
        const updatedLocalCart = cartItems.map((item) => ({
          id: item.id,
          quantity: item.quantity,
        }));
        updateLocalCart(updatedLocalCart);
      }

      const updatedCartItems = cartItems.map((item) => ({
        ...item,
        originalQuantity: item.quantity,
      }));
      setCartItems(updatedCartItems);
      setIsCartUpdated(false);
      setIsCheckoutEnabled(true);
    } catch (error) {
      console.error("Error updating cart:", error);
      setToastMessage("Failed to update cart.");
      setToastColor("error");
      setSnackbarOpen(true);
    }
  };

  const handleCheckout = () => {
    if (!isLoggedIn) {
      setToastMessage("Please Login/Register to proceed to checkout.");
      setToastColor("warning");
      setSnackbarOpen(true);
      return;
    }
    navigate("/checkoutpage");
  };

  const handleRemoveProduct = async (id) => {
    const itemToRemove = cartItems.find((item) => item.id === id);
    if (!itemToRemove) return;

    const updatedCartItems = cartItems.filter((item) => item.id !== id);
    setCartItems(updatedCartItems);
    setIsCartUpdated(true);

    if (isLoggedIn && hasExistingOrder && OrderRecordId) {
      const updatedLineItems = updatedCartItems.map((item) => ({
        recordid: item.recordid,
        name: item.name,
        product: item.id,
        quantity: item.quantity,
        unitpriceexclvat: item.unitpriceexclvat,
        vat: item.vat || 0,
      }));

      const payload = {
        order: {
          recordid: OrderRecordId,
          type: "Order",
          stage: "In Progress",
        },
        deallineitems: updatedLineItems,
        remove: [itemToRemove.recordid],
      };

      try {
        const response = await OrderUpdateApi(payload);
        setToastMessage(response.message || "Item removed from cart!");
        setToastColor(response.success ? "success" : "warning");
        setSnackbarOpen(true);
        updateDealState(response.data);
        await fetchCurrentDeals();
        setIsCheckoutEnabled(updatedCartItems.length > 0);
        getAndUpdateCartCount();
      } catch (error) {
        console.error("Error removing item:", error);
        setToastMessage("Failed to remove item.");
        setToastColor("error");
        setSnackbarOpen(true);
        setCartItems(cartItems); // Revert on error
      }
    } else if (!isLoggedIn) {
      const updatedLocalCart = updatedCartItems.map((item) => ({
        id: item.id,
        quantity: item.quantity,
      }));
      updateLocalCart(updatedLocalCart);
      setToastMessage("Item removed from cart!");
      setToastColor("success");
      setSnackbarOpen(true);
      setIsCheckoutEnabled(updatedCartItems.length > 0);
    }
  };

  return (
    <Container style={{ padding: "20px", fontFamily: "Arial, sans-serif", marginTop: "120px" }}>
      {loading ? (
        <Typography>Loading Cart...</Typography>
      ) : (
        <>
          <TableContainer component={Paper} style={{ marginBottom: "20px" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell>Price (Incl. Discount)</TableCell>
                  <TableCell>VAT</TableCell>
                  <TableCell>Unit Price (Incl. VAT)</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Subtotal</TableCell>
                  <TableCell>Remove</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cartItems.length > 0 ? (
                  cartItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>{formatPrice(item.unitpriceincldiscount)}</TableCell>
                      <TableCell>{item.vat}%</TableCell>
                      <TableCell>{formatPrice(item.priceWithVat)}</TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                          inputProps={{ min: 1 }}
                          style={{ width: "80px" }}
                        />
                      </TableCell>
                      <TableCell>{calculateSubtotal(item.priceWithVat, item.quantity)}</TableCell>
                      <TableCell>
                        <IconButton color="error" onClick={() => handleRemoveProduct(item.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} style={{ textAlign: "center" }}>
                      Your cart is empty. <Link to="/shop">Continue shopping</Link>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              borderRadius: "12px",
              padding: "15px 25px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
              marginBottom: "20px",
              maxWidth: "350px",
              ml: "auto",
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "700",
                  letterSpacing: "0.5px",
                  mb: 0.5,
                }}
              >
                Cart Total
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                <Typography sx={{ fontWeight: "500", color: "#555" }}>Total:</Typography>
                <Typography sx={{ fontWeight: "700", color: "black" }}>
                  {calculateTotal()}
                </Typography>
              </Box>
            </Box>
          </Box>

          {cartItems.length > 0 && (
            <Grid container spacing={2} justifyContent="flex-end" style={{ marginBottom: "40px" }}>
              <Grid item>
                <Button
                  variant="contained"
                  onClick={handleUpdateCart}
                  disabled={!isCartUpdated}
                  sx={{
                    backgroundColor: buttonColor,
                    color: "#fff",
                    textTransform: "none",
                    padding: "5px 15px",
                    borderRadius: "4px",
                    "&:hover": { backgroundColor: buttonHoverColor },
                  }}
                >
                  Update Cart
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  onClick={handleCheckout}
                  disabled={!isCheckoutEnabled}
                  sx={{
                    backgroundColor: buttonColor,
                    color: "#fff",
                    textTransform: "none",
                    padding: "5px 15px",
                    borderRadius: "4px",
                    "&:hover": { backgroundColor: buttonHoverColor },
                  }}
                >
                  Proceed to Checkout
                </Button>
              </Grid>
            </Grid>
          )}
        </>
      )}

      <UtilToastMessages
        isOpen={isSnackbarOpen}
        handleClose={() => setSnackbarOpen(false)}
        data={toastMessage}
        severity={toastColor}
      />
    </Container>
  );
};

export default CartPage;